import { AppState } from "../data/redux-state"
import { SVG_LEGEND_CLASS } from "../components/chart/drawLegendOverlay"
import { downloadPng } from "./downloadPng"
import hkGroteskRegularWoff2 from "../assets/fonts/HKGrotesk-Regular.woff2"

// Export at a fixed 2x resolution so PNG quality (logos, SVG rasterization)
// does not depend on the user's devicePixelRatio or browser zoom.
const EXPORT_SCALE = 2

// html2canvas rasterizes the chart SVG by serializing it to a standalone
// image, where document webfonts are unavailable and SVG text falls back to
// the browser default font. Embedding the legend font as a data-URI
// @font-face inside the SVG keeps the exported legend text identical to the
// on-screen rendering.
let cachedLegendFontCss: string | null = null
async function getLegendFontFaceCss(): Promise<string | null> {
  if (cachedLegendFontCss) {
    return cachedLegendFontCss
  }
  try {
    const response = await fetch(hkGroteskRegularWoff2)
    const buffer = await response.arrayBuffer()
    const bytes = new Uint8Array(buffer)
    let binary = ""
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i])
    }
    cachedLegendFontCss =
      `@font-face{font-family:"HKGrotesk_regular";` +
      `src:url(data:font/woff2;base64,${btoa(binary)}) format("woff2");}`
    return cachedLegendFontCss
  } catch (error) {
    console.error("PNG export: could not embed legend font", error)
    return null
  }
}

export async function downloadChartPng(props: {
  appState: AppState
  screenshotTarget: HTMLElement
}) {
  const { appState, screenshotTarget } = props
  const screenshotRect = screenshotTarget.getBoundingClientRect()
  const titleElement = screenshotTarget.querySelector(".above_chart_title") as HTMLElement | null
  const yAxisElement = screenshotTarget.querySelector("#y-axis") as SVGGraphicsElement | null
  const xAxisElement = screenshotTarget.querySelector("#x-axis") as SVGGraphicsElement | null
  const legendRectElement = screenshotTarget.querySelector(
    `rect.${SVG_LEGEND_CLASS}`
  ) as SVGGraphicsElement | null
  const titleRect = titleElement?.getBoundingClientRect()
  const yAxisRect = yAxisElement?.getBoundingClientRect()
  const xAxisRect = xAxisElement?.getBoundingClientRect()
  const legendRect = legendRectElement?.getBoundingClientRect()
  const exportPadding = 6
  const cropLeft = Math.max(
    0,
    Math.min(
      titleRect?.left ?? screenshotRect.left,
      yAxisRect?.left ?? screenshotRect.left,
      xAxisRect?.left ?? screenshotRect.left,
      legendRect?.left ?? screenshotRect.left
    ) - screenshotRect.left - exportPadding
  )
  const cropRight = Math.min(
    screenshotRect.width,
    Math.max(
      titleRect?.right ?? screenshotRect.right,
      yAxisRect?.right ?? screenshotRect.right,
      xAxisRect?.right ?? screenshotRect.right,
      legendRect?.right ?? screenshotRect.right
    ) - screenshotRect.left + exportPadding
  )

  const { default: html2canvas } = await import("html2canvas")
  const legendFontCss = await getLegendFontFaceCss()

  const canvas = await html2canvas(screenshotTarget, {
    useCORS: true,
    scale: EXPORT_SCALE,
    onclone: (clonedDocument) => {
      const clonedContainer = clonedDocument.getElementById("screenshotcontainer")

      if (legendFontCss) {
        clonedContainer?.querySelectorAll("svg").forEach((clonedSvg) => {
          const style = clonedDocument.createElementNS(
            "http://www.w3.org/2000/svg",
            "style"
          )
          style.textContent = legendFontCss
          clonedSvg.insertBefore(style, clonedSvg.firstChild)
        })
      }

      // The chart SVG (including its legend) is screenshot as-is, so the PNG
      // matches the on-screen rendering. Only the HTML tooltips need a fix:
      // html2canvas paints their blurred box-shadow as a solid teal wash, so
      // replace it with a crisp border for the export.
      clonedContainer?.querySelectorAll(".Tooltip").forEach((element) => {
        const tooltip = element as HTMLElement
        tooltip.style.boxShadow = "none"
        tooltip.style.border = "1px solid #c8c8c8"
      })
    },
  })

  const horizontalScale = canvas.width / screenshotRect.width
  const scaledCropLeft = Math.round(cropLeft * horizontalScale)
  const scaledCropRight = Math.round(cropRight * horizontalScale)
  const scaledCropWidth = Math.max(1, scaledCropRight - scaledCropLeft)

  const croppedCanvas = document.createElement("canvas")
  croppedCanvas.width = scaledCropWidth
  croppedCanvas.height = canvas.height

  const croppedContext = croppedCanvas.getContext("2d")
  if (!croppedContext) {
    return
  }

  croppedContext.drawImage(
    canvas,
    scaledCropLeft,
    0,
    scaledCropWidth,
    canvas.height,
    0,
    0,
    scaledCropWidth,
    canvas.height
  )

  downloadPng(appState, croppedCanvas.toDataURL("image/png"))
}
